import {Injectable} from '@angular/core';
import {Util} from "@data/util/util";
import {Activity, ActivityLineChartData, AdditionalActivity} from "@data/interefaces/client-portal.interfaces";
import {KpiSidebarItem} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {TimeUnit} from "@data/enums/data.enums";
import {IRowNode} from "@ag-grid-community/core";

@Injectable({
  providedIn: 'root'
})
export class ClientPortalActivityService {
  private TIME_UNIT_AMOUNT = 1;
  public CHART_DATA_AMOUNT: number = 10;
  public CHART_DATA_TYPES: { page:string, id: string, label: string, chartTitle: string , strokeColour: string}[] = [
    {page:'both', id: 'newSessions', label: 'New Sessions', chartTitle: 'New Sessions', strokeColour: 'green'},
    {page:'both', id: 'activeSessions', label: 'Active Sessions', chartTitle: 'Active Sessions' , strokeColour: 'blue'},
    {page:'both', id: 'avgSessionMinutes', label: 'Avg Session Duration', chartTitle: 'Avg Session Duration, in minutes' , strokeColour: 'orange'},
    {page:'pageActivity', id: 'numLoaded', label: 'Pages Loaded', chartTitle: 'Pages Loaded' , strokeColour: 'purple'},
    {page:'dashboardActivity', id: 'numLoaded', label: 'Dashboards Loaded', chartTitle: 'Dashboards Loaded', strokeColour: 'purple'},
  ];

  private AXIS_PROPERTIES: { id: string, title: string, position: string, keys: string[] }[] = [
    {id: 'countAxis', title: 'Count', position: 'left', keys: ['newSessions', 'activeSessions', 'numLoaded']},
    {id: 'minutesAxis', title: 'Minutes', position: 'right', keys: ['avgSessionMinutes']},
    {id: 'timeAxis', title: 'Time', position: 'bottom', keys: []}
  ];

  public convertResults(activityArr: Activity[]): Activity[] {
    return activityArr.map(activity => {
      return this.convertResult(activity);
    });
  }

  /* CONVERT DATA */
  public convertResult(activity: Activity): Activity {
    activity.sessionEndMethod = activity.sessionTimeout ? "Expired" : "Logout";
    activity.activityBreakdownDisplay = this.convertPageActivityBreakdown(activity.activityList);
    activity.sessionDuration = this.getDurationFor(activity.sessionStartTimestamp, activity.sessionEndTimestamp);
    activity.additionalActivityDuration = this.getAdditionalActivityDuration(activity.activityList);
    activity.loadDuration = this.getDurationFor(activity.loadStartDatetime, activity.loadEndDatetime);

    if (activity.loadDetails) {
      const dashboardDetailsObj =JSON.parse(activity.loadDetails);
      activity.deviceType = dashboardDetailsObj.deviceType ? dashboardDetailsObj.deviceType : '';
      activity.browserInformation = this.formatBrowserInformation(dashboardDetailsObj);
    }

    if (activity.sessionDetails) {
      activity.detailsObj = JSON.parse(activity.sessionDetails);
    }
    return activity;
  }

  private formatBrowserInformation(loadDetails:any ): string {
    return  (loadDetails.userAgent ? `User Agent: ${loadDetails.userAgent} ` : ``)
      + (loadDetails.screenResolution ? `<br> Screen Resolution: ${loadDetails.screenResolution}` : ``);
  }

  private convertPageActivityBreakdown(activityBreakdownList : AdditionalActivity[] | undefined  ): string {
    if (activityBreakdownList && activityBreakdownList.length > 0) {
      return activityBreakdownList.map(activity => {
        return `${Util.getFormattedDateWithMilliseconds(activity.startDateTime)} - ${activity.type}: ${activity.details}`
      }).join('<br>');
    } else {
      return '';
    }
  }

  private getAdditionalActivityDuration(activityBreakdownList : AdditionalActivity[] | undefined  ) {
    if (activityBreakdownList && activityBreakdownList.length > 0) {
      let duration = 0;
      activityBreakdownList.forEach(activity => {
        let currentDuration = this.getDurationFor(activity.startDateTime, activity.endDateTime);
        if (currentDuration) {
          duration = duration + currentDuration;
        }
      });
      return duration;
    } else {
      return;
    }
  }

  /* Helpers */
  private getDurationFor(startDate: Date | undefined , endDate: Date | undefined){
    if( startDate) {
      return Util.calculateDurationInMilliSeconds(startDate, endDate ? endDate : new Date());
    }
    return;
  }

  public filterActivitiesBySessionStartInTimeRange(activities: Activity[], startDate: Date, endDate: Date | undefined){
    return activities
      .filter(activity => {
        return new Date(activity.sessionStartTimestamp) >= startDate
          && (endDate ? new Date(activity.sessionStartTimestamp) < endDate : true)
      });
  }

  public getCountUniqueSessionIdsInActivities(activities:Activity[]) {
    return activities
      .reduce((sessionIds: number[], activity) => {
        if (!sessionIds.some(id => id === activity.sessionId)) {
          sessionIds.push(activity.sessionId);
        }
        return sessionIds;
      }, [])
      .length
  }

  public getTimestampAsDate(timeStamp: Date|undefined|null){
    return timeStamp ? new Date(timeStamp) : new Date();
  }

  public getAverageSessionDuration(activitiesArr:Activity[]){
    let activities = activitiesArr
      .filter(activity => activity.sessionDuration !== null)
      .filter((activity, index, self) =>
        index === self.findIndex((t) => t.sessionId === activity.sessionId))
      .map(activity => { return activity.sessionDuration ? activity.sessionDuration : 0} );

    return Util.calculateAverageFromNumberArr(activities);
  }

  public getAverageLoadDuration(activitiesArr: Activity[]){
      let activityDurations = activitiesArr
        .filter(activity => activity.loadDuration != null)
        .map(activity => { return activity.loadDuration ? activity.loadDuration : 0});

      return Util.calculateAverageFromNumberArr(activityDurations);
  }

  /* KPIs */
  public getAverageLoadTimeKpi(activities: Activity[], timeUnit: TimeUnit, title:string, css_id:string): KpiSidebarItem {
    const averageLoadTimeFunct = (timeUnitStart: Date): string =>
      Util.formatMillisecondDuration(this.getAverageLoadDuration(
        this.filterActivitiesBySessionStartInTimeRange(activities, timeUnitStart, undefined)
      ));

    return this.createKpiItem(timeUnit, title,  css_id, this.TIME_UNIT_AMOUNT, averageLoadTimeFunct);
  }

  public createKpiItem(timeUnit:TimeUnit, baseTitle:string, id:string, numberOfTimeUnits:number, func: (startTime: Date) => number | string):KpiSidebarItem{
    let timeStart = Util.getTimeUnitStartTimeFromCurrent(timeUnit, numberOfTimeUnits);
    let value = func(timeStart);
    return ({
      id: id,
      title: `${baseTitle} ${Util.getTimeUnitTitleString(timeUnit, numberOfTimeUnits)}`,
      value: value
    });
  }

  /* CHARTS */
  public getChartOptionsWithoutGroups(chartData: ActivityLineChartData[], scale:TimeUnit, title:string, page:string) {
    const series = this.getSeriesConfig(page);
    const axes = this.getAxesConfig(scale);
    return  {
      title: {
        text: title,
      },
      data: chartData,
      series: series,
      axes: axes,
      legend: {
        position: 'bottom'
      }
    };
  }

  public getChartOptionsWithGroup(groupedChartData:any, scale: TimeUnit, selectedDataType:any, page:string) {
    const mergedGroupedData = this.mergeGroupedChartDataByDataType(groupedChartData, selectedDataType);
    const title = this.getChartDataType(selectedDataType, page)?.chartTitle;
    const isCount = ['newSessions', 'activeSessions', 'numLoaded'].includes(selectedDataType);
    const yAxisLabel = isCount ? 'Count' : 'Minutes';
    const series = Object.keys(groupedChartData).map(groupName => ({
      type: isCount ? 'area' : 'line',
      xKey: 'time',
      yKey: groupName,
      title: groupName,
      stacked: isCount,
      marker: {enabled: false},
    }));

    const axes = [
      this.createXAxisConfig(scale),
      {
        type: 'number',
        position: 'left',
        title: {text: yAxisLabel},
      },
      {
        type: 'number',
        position: 'right',
        title: {text: yAxisLabel},
      }
    ];
    return {
      title: {
        text: title,
      },
      data: mergedGroupedData,
      series: series,
      axes: axes,
      legend: {
        position: 'bottom'
      }
    }
  }

  /* Line Chart */
  public getGroupedActivityLineChartData(groupedMap: Map<string, Activity[]>, timeUnit: TimeUnit, dataAmount: number): { [key: string]: ActivityLineChartData[] } {
    const result: { [key: string]: ActivityLineChartData[] } = {};
    groupedMap.forEach((activities, group) => {
      result[group] = this.getActivityLineChartData(activities, timeUnit, dataAmount);
      let print = result[group].map(chart => {
       return `${chart.time} - ${chart.newSessions}`}).concat();
      console.log(`Result for ${group} is ${print}`);
    });
    return result;
  }

  public getActivityLineChartData(activities: Activity[], timeUnit: TimeUnit, dataAmount: number): ActivityLineChartData[] {
    const chartData: ActivityLineChartData[] = [];

    for (let i = dataAmount; i >= 0; i--) {

      const periodStart = Util.getTimeUnitStartTimeFromCurrent(timeUnit, i + 1);
      const periodEnd = Util.getTimeUnitStartTimeFromCurrent(timeUnit, i);

      // session started within this period
      const newSessions = this.getCountUniqueSessionIdsInActivities(
        this.filterActivitiesBySessionStartInTimeRange(activities, periodStart, periodEnd));

      // session ended within this period
      const activitiesEndingInRange = activities
          .filter(activity => {
            return this.getTimestampAsDate(activity.sessionEndTimestamp) >= periodStart
            && this.getTimestampAsDate(activity.sessionEndTimestamp) < periodEnd
          });
      const activeSessions = this.getCountUniqueSessionIdsInActivities(activitiesEndingInRange);
      const avgSessionMinutes = this.msToMin(this.getAverageSessionDuration(activitiesEndingInRange));

      // load activity within this period
      const numLoaded = activities
        .filter(activity => {
          return this.getTimestampAsDate(activity.loadStartDatetime) >= periodStart
            && this.getTimestampAsDate(activity.loadStartDatetime) < periodEnd
        }).length
      ;

      chartData.push({
        time: i,
        newSessions,
        activeSessions,
        avgSessionMinutes,
        numLoaded
      });
    }
    return chartData;
  }

  public msToMin(totalMilliSeconds:number){
    const msInAMin = (60*1000);
    return  Math.floor((totalMilliSeconds)/ msInAMin);
  }

  public getChartDataType(value:string, page:string){
    return this.CHART_DATA_TYPES
      .find(dataType => dataType.id === value && this.checkPage(page, dataType))
  }

  public getChartDataTypesFor(page:string):{}{
    return this.CHART_DATA_TYPES.filter(dataType => this.checkPage(page, dataType));
  }

  private checkPage(page:string, dataType: any) :boolean {
    return dataType.page ? (dataType.page === page || dataType.page === 'both') : false;
  }

  // Series
  public getSeriesConfig(page:string): any[] {
    return [
      this.seriesConfig('newSessions', page),
      this.seriesConfig('activeSessions', page),
      this.seriesConfig('avgSessionMinutes', page),
      this.seriesConfig('numLoaded', page),
    ];
  }

  public seriesConfig(yKey:string, page:string) : {} {
    const config= this.getChartDataType(yKey,page)
    return {
      type: 'line',
      xKey: 'time',
      yKey: yKey,
      yName: config?.chartTitle,
      stroke: config?.strokeColour,
      marker: {enabled: false},
    }
  }

  // Axis
  public getAxesConfig(timeUnit: TimeUnit): any[] {
    return [
      this.createXAxisConfig(timeUnit),
      this.createYAxisConfig('countAxis'),
      this.createYAxisConfig('minutesAxis'),
      this.createYAxisConfig('msAxis')
    ];
  }

  private createYAxisConfig(id:string) : {} {
    const axisProperties = this.AXIS_PROPERTIES.find(axis => axis.id === id);
    return {
      type:'number',
      position: axisProperties?.position,
      title: {text: axisProperties?.title},
      id: id,
      keys: axisProperties?.keys,
      gridLine: {
        enabled: true,
      }
    }
  }

  private createXAxisConfig(timeUnit: TimeUnit): any {
    const axisProperties = this.AXIS_PROPERTIES.find(axis => axis.id === 'timeAxis');
    return {
      type: 'number',
      position: axisProperties?.position,
      title: {text: axisProperties?.title},
      min: 0,
      max: 10,
      label: {
        formatter: (params: any) => Util.formatChartTimeLabel(params.value, timeUnit),
      },
      reverse: true,
      interval: {step: 1},
    };
  }

  public collectRowsUnderGroup(groupNode: IRowNode, rows: Activity[]) {
    groupNode.childrenAfterFilter?.forEach(childNode => {
      if (childNode.group) {
        this.collectRowsUnderGroup(childNode, rows);
      } else {
        rows.push(childNode.data);
      }
    });
  }

  private mergeGroupedChartDataByDataType(groupedChartData: { [key: string]: ActivityLineChartData[] }, selectedDataType:any) {
    const mergedData: { [key: number]: any } = {};

    Object.keys(groupedChartData).forEach(group => {
      groupedChartData[group].forEach(chartData => {
        const time = chartData.time;
        if (!mergedData[time]) {
          mergedData[time] = {time};
        }

        mergedData[time][group] = chartData[selectedDataType as keyof ActivityLineChartData];
      });
    });
    return Object.values(mergedData);
  }
}
